import { PersistenceValidationError } from '../../application/errors/PersistenceError';
import type { BackupTransactions } from '../../application/ports/BackupTransactions';
import { EXPORT_ENVELOPE_VERSION, type ExportEnvelope } from '../../domain/backup/ExportEnvelope';
import type { ProfileId } from '../../domain/shared/ids';
import type { AscendInvictusDatabase } from './AscendInvictusDatabase';
import {
  AppPreferencesMapper,
  DailyRoutineMapper,
  DailyStepMapper,
  ExerciseLogMapper,
  JournalEntryMapper,
  ProfileMapper,
  RoutineStepTemplateMapper,
  RoutineTemplateMapper,
  WorkoutSessionMapper,
} from './mappers';
import { executePersistence } from './translatePersistenceError';

const PROFILE_SCOPED_TABLES = [
  'routineTemplates',
  'routineStepTemplates',
  'dailyRoutines',
  'dailySteps',
  'journalEntries',
  'workoutSessions',
  'exerciseLogs',
  'appPreferences',
] as const;

export class DexieBackupTransactions implements BackupTransactions {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  public exportProfileData(profileId: ProfileId, exportedAt: string): Promise<ExportEnvelope> {
    return executePersistence('read', async () => {
      const profileRecord = await this.database.profiles.get(profileId);
      if (!profileRecord) throw new PersistenceValidationError();

      const [
        routineTemplateRecords,
        routineStepTemplateRecords,
        dailyRoutineRecords,
        dailyStepRecords,
        journalEntryRecords,
        workoutSessionRecords,
        exerciseLogRecords,
        appPreferencesRecords,
      ] = await Promise.all([
        this.database.routineTemplates.where('profileId').equals(profileId).toArray(),
        this.database.routineStepTemplates.where('profileId').equals(profileId).toArray(),
        this.database.dailyRoutines.where('profileId').equals(profileId).toArray(),
        this.database.dailySteps.where('profileId').equals(profileId).toArray(),
        this.database.journalEntries.where('profileId').equals(profileId).toArray(),
        this.database.workoutSessions.where('profileId').equals(profileId).toArray(),
        this.database.exerciseLogs.where('profileId').equals(profileId).toArray(),
        this.database.appPreferences.where('profileId').equals(profileId).toArray(),
      ]);

      return {
        version: EXPORT_ENVELOPE_VERSION,
        exportedAt,
        profile: ProfileMapper.fromRecord(profileRecord),
        routineTemplates: routineTemplateRecords.map((record) => RoutineTemplateMapper.fromRecord(record)),
        routineStepTemplates: routineStepTemplateRecords.map((record) =>
          RoutineStepTemplateMapper.fromRecord(record),
        ),
        dailyRoutines: dailyRoutineRecords.map((record) => DailyRoutineMapper.fromRecord(record)),
        dailySteps: dailyStepRecords.map((record) => DailyStepMapper.fromRecord(record)),
        journalEntries: journalEntryRecords.map((record) => JournalEntryMapper.fromRecord(record)),
        workoutSessions: workoutSessionRecords.map((record) => WorkoutSessionMapper.fromRecord(record)),
        exerciseLogs: exerciseLogRecords.map((record) => ExerciseLogMapper.fromRecord(record)),
        appPreferences: appPreferencesRecords.map((record) => AppPreferencesMapper.fromRecord(record)),
      };
    });
  }

  public replaceProfileData(profileId: ProfileId, envelope: ExportEnvelope): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        [
          this.database.profiles,
          this.database.routineTemplates,
          this.database.routineStepTemplates,
          this.database.dailyRoutines,
          this.database.dailySteps,
          this.database.journalEntries,
          this.database.workoutSessions,
          this.database.exerciseLogs,
          this.database.appPreferences,
        ],
        async () => {
          const existingProfile = await this.database.profiles.get(profileId);
          if (!existingProfile) throw new PersistenceValidationError();
          const updatedProfile = ProfileMapper.toRecord({
            ...ProfileMapper.fromRecord(existingProfile),
            displayName: envelope.profile.displayName,
            locale: envelope.profile.locale,
            timeZone: envelope.profile.timeZone,
          });

          for (const table of PROFILE_SCOPED_TABLES) {
            await this.database[table].where('profileId').equals(profileId).delete();
          }

          await this.database.profiles.put(updatedProfile);
          await this.database.routineTemplates.bulkAdd(
            envelope.routineTemplates.map((entity) =>
              RoutineTemplateMapper.toRecord({ ...entity, profileId }),
            ),
          );
          await this.database.routineStepTemplates.bulkAdd(
            envelope.routineStepTemplates.map((entity) =>
              RoutineStepTemplateMapper.toRecord({ ...entity, profileId }),
            ),
          );
          await this.database.dailyRoutines.bulkAdd(
            envelope.dailyRoutines.map((entity) =>
              DailyRoutineMapper.toRecord({ ...entity, profileId }),
            ),
          );
          await this.database.dailySteps.bulkAdd(
            envelope.dailySteps.map((entity) => DailyStepMapper.toRecord({ ...entity, profileId })),
          );
          await this.database.journalEntries.bulkAdd(
            envelope.journalEntries.map((entity) =>
              JournalEntryMapper.toRecord({ ...entity, profileId }),
            ),
          );
          await this.database.workoutSessions.bulkAdd(
            envelope.workoutSessions.map((entity) =>
              WorkoutSessionMapper.toRecord({ ...entity, profileId }),
            ),
          );
          await this.database.exerciseLogs.bulkAdd(
            envelope.exerciseLogs.map((entity) => ExerciseLogMapper.toRecord({ ...entity, profileId })),
          );
          await this.database.appPreferences.bulkAdd(
            envelope.appPreferences.map((entity) =>
              AppPreferencesMapper.toRecord({ ...entity, profileId }),
            ),
          );
        },
      );
    });
  }
}
